import {useMemo, useSyncExternalStore} from 'react';
import {useSelector} from 'react-redux';
import {desktopViewSelector} from '../store/selectors/layout.selectors';

const MOBILE_BREAKPOINT = 767;
const TABLET_BREAKPOINT = 1160;
const RESIZE_DEBOUNCE_MS = 100;

export type Layout = {
    mobileView: boolean;
    tabletScreenSize: boolean;
    mobileScreenSize: boolean;
};

type ViewportSize = Pick<Layout, 'mobileScreenSize' | 'tabletScreenSize'>;

const DEFAULT_VIEWPORT: ViewportSize = {mobileScreenSize: false, tabletScreenSize: false};

function measureViewport(): ViewportSize {
    if (typeof window === 'undefined') return DEFAULT_VIEWPORT;
    const mobileScreenSize = window.innerWidth < MOBILE_BREAKPOINT;
    return {mobileScreenSize, tabletScreenSize: !mobileScreenSize && window.innerWidth < TABLET_BREAKPOINT};
}

let viewport: ViewportSize = measureViewport();
const listeners = new Set<() => void>();
let debounceHandle: ReturnType<typeof setTimeout> | undefined;

function emitIfChanged(): void {
    const next = measureViewport();
    if (next.mobileScreenSize === viewport.mobileScreenSize && next.tabletScreenSize === viewport.tabletScreenSize) {
        return;
    }
    viewport = next;
    listeners.forEach((listener) => listener());
}

function handleResize(): void {
    if (debounceHandle !== undefined) clearTimeout(debounceHandle);
    debounceHandle = setTimeout(() => {
        debounceHandle = undefined;
        emitIfChanged();
    }, RESIZE_DEBOUNCE_MS);
}

if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('resize', handleResize);
}

function subscribeViewport(onStoreChange: () => void): () => void {
    listeners.add(onStoreChange);
    return () => {
        listeners.delete(onStoreChange);
    };
}

function getViewportSnapshot(): ViewportSize {
    return viewport;
}

export function useLayout(): Layout {
    const {mobileScreenSize, tabletScreenSize} = useSyncExternalStore(subscribeViewport, getViewportSnapshot);

    const isDesktopViewSelected = useSelector(desktopViewSelector);

    const mobileView = useMemo(() => mobileScreenSize && !isDesktopViewSelected, [mobileScreenSize, isDesktopViewSelected]);

    return {mobileView, tabletScreenSize, mobileScreenSize};
}
